import { loadData, saveData } from "@/utils/storageHelper";
import store from "@/store";
import axios from "axios";
const config = require("../../config");

// We want kiosks to reload nightly, but *staggered* so a whole location doesn't refresh at once.
// The previous implementation called `/api/system/*` endpoints that don't exist in this repo,
// so the reload never happened. We instead do a deterministic, per-device browser reload.

const DEFAULT_RESTART_HOUR_LOCAL = 2; // local device time (2am)
const RESTART_WINDOW_SECONDS = 60 * 60; // 1 hour window (HH:00:00 - HH:59:59) in local device time
const RESTART_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour
const BUSY_RETRY_DELAY_MS = 5 * 60 * 1000; // 5 minutes
const UNAVAILABLE_RETRY_BASE_MS = 2 * 60 * 1000; // 2 minutes
const UNAVAILABLE_RETRY_JITTER_SECONDS = 8 * 60; // +0..8 minutes (per-device) to prevent herd after outages
const HEALTHCHECK_TIMEOUT_MS = 2500; // fast-fail: if API isn't reachable quickly, don't reload
const FORCE_RELOAD_INTERVAL_DAYS = 7; // Max days to go without a reload (prevents memory leaks)
const UPDATE_CHECK_RETRY_MS = 10 * 60 * 1000; // 10 minutes: keep checking for new version within the selected hour

let lastRestartAttempt = 0;
let scheduledTimeoutId = null;
let initialETag = null;

// Immediately capture the current version's ETag on module load (app startup)
captureInitialETag();

// Update the "Last Reload" timestamp on startup.
// This ensures that "Last Update" in the UI reflects the time this specific session started,
// effectively resetting the "days since last reload" counter (since memory is fresh).
saveData('lastNightlyReload', Date.now());

export function scheduleDailyRestart() {
  const now = new Date();
  const restartTime = getNextRestartTime(now);

  // Avoid multiple timers if router hooks fire more than once.
  if (scheduledTimeoutId) {
    clearTimeout(scheduledTimeoutId);
    scheduledTimeoutId = null;
  }

  console.log(`[scheduleDailyRestart] Next reload scheduled for: ${restartTime.toString()}`);

  const delay = Math.max(restartTime.getTime() - now.getTime(), 0);
  scheduledTimeoutId = setTimeout(() => {
    checkAndRestart().catch((err) => {
      console.error("[scheduleDailyRestart] Unexpected error in nightly reload:", err);
      scheduleDailyRestart();
    });
  }, delay);
}

export function getLastReloadTime() {
  return loadData('lastNightlyReload');
}

export async function manualCheckForUpdate() {
  const hasUpdate = await checkForNewVersion();
  if (hasUpdate) {
    console.log("[manualCheckForUpdate] Update detected. Reloading.");
    saveData('lastNightlyReload', Date.now());
    window.location.reload();
    return { updated: true };
  }
  return { updated: false };
}

/**
 * Check if an update is available without reloading.
 * Returns { available: true } if a new version is detected.
 */
export async function checkUpdateAvailable() {
  const hasUpdate = await checkForNewVersion();
  return { available: hasUpdate };
}

/**
 * Perform the reload after user confirmation.
 */
export function performReload() {
  saveData('lastNightlyReload', Date.now());
  window.location.reload();
}

function getNextRestartTime(now) {
  // Use a stable per-device identifier so each terminal gets a consistent offset each day.
  // Prefer live store state, fall back to storage.
  const deviceId = store?.state?.terminal?.deviceId || loadData("deviceId") || "unknown-device";
  const restartHour = getRestartHourLocal();
  const windowStart = getRestartWindowStartFor(now);
  const windowEnd = getRestartWindowEndFor(now);

  // If we're currently inside today's window, schedule a near-immediate check
  // (we may have started the app after the original per-device offset passed).
  if (now >= windowStart && now <= windowEnd) {
    const catchUpDelaySeconds = stableOffsetSeconds(`${deviceId}:${formatLocalDateKey(now)}:catchup`, 60);
    const t = new Date(now);
    t.setSeconds(t.getSeconds() + catchUpDelaySeconds);
    return t;
  }

  // If we're before today's window, schedule inside the window using a stable per-day offset.
  if (now < windowStart) {
    const dateKey = formatLocalDateKey(now); // YYYY-MM-DD in local time
    const offsetSeconds = stableOffsetSeconds(`${deviceId}:${dateKey}`, RESTART_WINDOW_SECONDS);
    const restartTime = new Date(windowStart);
    restartTime.setSeconds(restartTime.getSeconds() + offsetSeconds);
    return restartTime;
  }

  // Otherwise, schedule tomorrow's window with a stable per-day offset.
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowKey = formatLocalDateKey(tomorrow);
  const tomorrowOffsetSeconds = stableOffsetSeconds(`${deviceId}:${tomorrowKey}`, RESTART_WINDOW_SECONDS);
  const restartTime = new Date(tomorrow);
  restartTime.setHours(restartHour, 0, 0, 0);
  restartTime.setSeconds(restartTime.getSeconds() + tomorrowOffsetSeconds);

  return restartTime;
}

function getRestartHourLocal() {
  const raw = store?.state?.location?.config?.dailyRestartHourLocal;
  const hour = Number.isFinite(Number(raw)) ? parseInt(raw, 10) : null;
  if (hour === null || Number.isNaN(hour) || hour < 0 || hour > 23) {
    return DEFAULT_RESTART_HOUR_LOCAL;
  }
  return hour;
}

function formatLocalDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function stableOffsetSeconds(seed, moduloSeconds) {
  // Simple deterministic hash (djb2-ish) -> [0, moduloSeconds)
  let hash = 5381;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) + hash) + seed.charCodeAt(i);
    hash = hash | 0; // force 32-bit
  }
  const positive = Math.abs(hash);
  return positive % moduloSeconds;
}

function shouldAvoidReloadRightNow() {
  // Avoid reloading in the middle of an order/payment flow.
  // Using live Vuex state is safer than localStorage which might be stale.
  try {
    if (!store || !store.getters) return false;
    
    const started = store.getters['order/started'];
    const complete = store.getters['order/complete'];
    
    // If an order is started but NOT complete, we are busy.
    return Boolean(started && !complete);
  } catch (e) {
    console.warn("[scheduleDailyRestart] Error reading store state, assuming busy to be safe:", e);
    return true; // Fail-safe: don't reload if we can't determine state
  }
}

function isInCooldownPeriod() {
  return Date.now() - lastRestartAttempt < RESTART_COOLDOWN_MS;
}

function getRestartWindowStartFor(date) {
  const start = new Date(date);
  start.setHours(getRestartHourLocal(), 0, 0, 0);
  return start;
}

function getRestartWindowEndFor(date) {
  const end = getRestartWindowStartFor(date);
  end.setSeconds(end.getSeconds() + RESTART_WINDOW_SECONDS);
  return end;
}

function getBaseUrl() {
  const baseHost = config.API_PORT ? `${config.API_HOST}:${config.API_PORT}` : config.API_HOST;
  const protocol = config.API_HOST === "localhost" ? "http" : "https";
  return `${protocol}://${baseHost}/`;
}

function getFrontendOrigin() {
  return window.location.origin;
}

async function captureInitialETag() {
  try {
    const origin = getFrontendOrigin();
    const resp = await axios.head(`${origin}/`, {
      timeout: HEALTHCHECK_TIMEOUT_MS
    });
    // ETag is standard, but some servers use Last-Modified.
    // We concatenate both to be sure.
    const etag = resp.headers['etag'] || '';
    const lastModified = resp.headers['last-modified'] || '';
    initialETag = `${etag}-${lastModified}`;
    console.log(`[scheduleDailyRestart] Captured initial version tag: ${initialETag}`);
  } catch (e) {
    console.warn("[scheduleDailyRestart] Failed to capture initial ETag, defaulting to always-reload mode.", e);
    initialETag = null;
  }
}

async function isBackendAvailable() {
  // If the browser reports offline, treat backend as unavailable.
  if (navigator && navigator.onLine === false) return false;

  try {
    const baseUrl = getBaseUrl();
    const response = await axios.get(`${baseUrl}health/cache`, {
      timeout: HEALTHCHECK_TIMEOUT_MS
    });
    return response.status === 200 && response.data && response.data.ok;
  } catch (e) {
    // Timeout or network error
    return false;
  }
}

async function checkForNewVersion() {
  // If we never captured an initial tag, we assume we should reload (fallback behavior).
  if (!initialETag) return true;

  try {
    const origin = getFrontendOrigin();
    // Add cache buster to be absolutely sure we hit the server
    const resp = await axios.head(`${origin}/?t=${Date.now()}`, {
      timeout: HEALTHCHECK_TIMEOUT_MS
    });
    
    const currentETag = resp.headers['etag'] || '';
    const currentLastModified = resp.headers['last-modified'] || '';
    const latestTag = `${currentETag}-${currentLastModified}`;

    if (latestTag !== initialETag) {
      console.log(`[scheduleDailyRestart] New version detected! (Old: ${initialETag}, New: ${latestTag})`);
      return true;
    }
    return false;
  } catch (e) {
    console.warn("[scheduleDailyRestart] Failed to check for new version:", e);
    return false; // Safety: if we can't check, assume no update to avoid broken reloads
  }
}

function getUnavailableRetryDelayMs() {
  const deviceId = store?.state?.terminal?.deviceId || loadData("deviceId") || "unknown-device";
  const jitterSeconds = stableOffsetSeconds(`${deviceId}:unavailable-retry`, UNAVAILABLE_RETRY_JITTER_SECONDS + 1);
  return UNAVAILABLE_RETRY_BASE_MS + (jitterSeconds * 1000);
}

async function checkAndRestart() {
  const now = new Date();
  const windowEnd = getRestartWindowEndFor(now);

  // Hard guard: only ever attempt within the nightly window.
  // If we missed the window (device slept, app backgrounded, etc.), wait until next night.
  if (now > windowEnd) {
    scheduleDailyRestart();
    return;
  }

  if (isInCooldownPeriod()) {
    scheduleDailyRestart();
    return;
  }

  if (shouldAvoidReloadRightNow()) {
    console.log("[scheduleDailyRestart] Order in progress; delaying reload.");
    const nextAttemptAt = Date.now() + BUSY_RETRY_DELAY_MS;
    if (nextAttemptAt > windowEnd.getTime()) {
      scheduleDailyRestart();
      return;
    }
    scheduledTimeoutId = setTimeout(() => checkAndRestart(), BUSY_RETRY_DELAY_MS);
    return;
  }

  const backendOk = await isBackendAvailable();
  if (!backendOk) {
    console.log("[scheduleDailyRestart] Offline or API unavailable; skipping reload for now.");
    const retryDelay = getUnavailableRetryDelayMs();
    const nextAttemptAt = Date.now() + retryDelay;
    if (nextAttemptAt > windowEnd.getTime()) {
      scheduleDailyRestart();
      return;
    }
    scheduledTimeoutId = setTimeout(() => checkAndRestart(), retryDelay);
    return;
  }

  // Smart Reload Logic:
  // 1. Check if it's been a long time (FORCE_RELOAD_INTERVAL_DAYS) -> Reload to clear memory.
  // 2. Check if there is a new version (ETag mismatch) -> Reload to update.
  // 3. Otherwise -> Skip reload.
  
  const lastReloadTime = loadData('lastNightlyReload') || 0;
  const daysSinceLastReload = (now.getTime() - lastReloadTime) / (1000 * 60 * 60 * 24);
  const isMemoryFlushDue = daysSinceLastReload >= FORCE_RELOAD_INTERVAL_DAYS;
  
  let shouldReload = isMemoryFlushDue;
  
  if (!shouldReload) {
    // Only check network if we don't already need to reload
    const hasUpdate = await checkForNewVersion();
    if (hasUpdate) {
      console.log("[scheduleDailyRestart] Update detected. Reloading.");
      shouldReload = true;
    }
  } else {
    console.log(`[scheduleDailyRestart] Memory flush due (Last reload: ${daysSinceLastReload.toFixed(1)} days ago). Reloading.`);
  }

  if (!shouldReload) {
    console.log("[scheduleDailyRestart] No update detected and memory flush not due. Skipping reload.");
    // Keep checking within the selected hour so that if a new version becomes available later,
    // we still auto-update within the same window.
    const nextAttemptAt = Date.now() + UPDATE_CHECK_RETRY_MS;
    if (nextAttemptAt > windowEnd.getTime()) {
      scheduleDailyRestart();
      return;
    }
    scheduledTimeoutId = setTimeout(() => checkAndRestart(), UPDATE_CHECK_RETRY_MS);
    return;
  }

  try {
    lastRestartAttempt = Date.now();
    // Save timestamp BEFORE reload so we don't loop if reload fails, 
    // but effectively we only care if reload succeeds.
    saveData('lastNightlyReload', Date.now()); 
    
    console.log("[scheduleDailyRestart] Reloading kiosk now.");
    window.location.reload();
  } catch (error) {
    console.error("[scheduleDailyRestart] Failed to reload:", error);
    scheduleDailyRestart();
  }
}
